import { useRef, useState } from "react";
import {
  Upload,
  FileSpreadsheet,
  Rows3,
  CheckCircle2,
  AlertTriangle,
  Download,
  Loader2,
  X,
  Ban,
} from "lucide-react";
import { LRDirectAtlasCard, LRDirectAtlasCardHeader } from "./LRDirectAtlasCard";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useShipperNetworks } from "@/hooks/useShipperNetworks";
import { useBulkPostDirectLoads } from "@/hooks/useBulkPostDirectLoads";
import {
  downloadLRDirectTemplateCSV,
  parseAndValidateDirectCSV,
  MAX_BULK_ROWS,
  SOFT_WARN_ROWS,
  type DirectBulkParseResult,
  type DirectSendTo,
  type DirectPostType,
} from "@/lib/lrDirect/bulkDirectUploadSchema";

/**
 * LR Direct ATLAS Bulk Upload — real CSV → validate → post to direct_loads.
 * LR Direct only: never imports from src/components/ai-post/* and never writes
 * to the LaneRunner `loads` table.
 */
export default function LRDirectBulkUploadCard() {
  const { toast } = useToast();
  const { user } = useAuth();
  const { networks, loading: networksLoading } = useShipperNetworks(user?.id);
  const { post, cancel, reset, posting, progress, summary } = useBulkPostDirectLoads();

  const fileRef = useRef<HTMLInputElement>(null);
  const [parsed, setParsed] = useState<DirectBulkParseResult | null>(null);
  const [sendTo, setSendTo] = useState<DirectSendTo>("private");
  const [postType, setPostType] = useState<DirectPostType>("load");
  const [networkId, setNetworkId] = useState<string>("");
  const [softWarn, setSoftWarn] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);

  const isDemo = (user?.email || "").toLowerCase().startsWith("demo@lanerunner");
  const defaultNetworkId = networkId || networks.find((n) => n.is_default)?.id || networks[0]?.id || "";
  const needsNetwork = sendTo === "private" || sendTo === "both";
  const routingBlocked = needsNetwork && !defaultNetworkId;

  const handleDownload = () => {
    downloadLRDirectTemplateCSV();
    toast({ title: "Template downloaded", description: "Fill in your lanes and upload the CSV." });
  };

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = parseAndValidateDirectCSV(reader.result as string);
      if (result.totalRows > MAX_BULK_ROWS) {
        toast({
          title: `Maximum ${MAX_BULK_ROWS} rows per upload`,
          description: `That file has ${result.totalRows} rows. Split it into smaller batches.`,
          variant: "destructive",
        });
        return;
      }
      setSoftWarn(
        result.totalRows > SOFT_WARN_ROWS
          ? `Large upload (${result.totalRows} rows) — posting may take a minute`
          : null,
      );
      setParsed(result);
      reset();
      setShowErrors(result.invalidRows.length > 0);
      toast({
        title: `${result.totalRows} row${result.totalRows !== 1 ? "s" : ""} parsed`,
        description: `${result.validRows.length} valid · ${result.invalidRows.length} with errors`,
      });
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const clearAll = () => {
    setParsed(null);
    setSoftWarn(null);
    setShowErrors(false);
    reset();
  };

  const handlePost = async () => {
    if (!parsed || !user) return;
    if (parsed.validRows.length === 0) {
      toast({ title: "Nothing to post", description: "Fix the row errors and re-upload.", variant: "destructive" });
      return;
    }
    if (routingBlocked) {
      toast({
        title: "Select a private network first",
        description:
          "This batch is routed to your private network. Nothing was posted — private loads are never sent to the open market by default.",
        variant: "destructive",
      });
      return;
    }
    const res = await post({
      parsed,
      userId: user.id,
      sendTo,
      postType,
      privateNetworkId: needsNetwork ? defaultNetworkId : null,
      isDemo,
    });
    toast({
      title: `${res.posted} post${res.posted !== 1 ? "s" : ""} live`,
      description:
        res.failed > 0 || res.skipped > 0
          ? `${res.failed} failed · ${res.skipped} skipped — see details below`
          : "All valid rows posted.",
      variant: res.failed > 0 ? "destructive" : "default",
    });
  };

  const stats = [
    { label: "Rows", value: parsed ? String(parsed.totalRows) : "—", icon: Rows3 },
    { label: "Valid", value: parsed ? String(parsed.validRows.length) : "—", icon: CheckCircle2 },
    { label: "Errors", value: parsed ? String(parsed.invalidRows.length) : "—", icon: AlertTriangle },
  ];

  const pct = progress ? Math.round((progress.current / progress.total) * 100) : 0;

  return (
    <LRDirectAtlasCard className="flex flex-col">
      <LRDirectAtlasCardHeader
        icon={<Upload className="h-3.5 w-3.5" style={{ color: "hsl(170 50% 15%)" }} />}
        title="ATLAS Bulk Upload"
      >
        <div className="flex items-center gap-1.5">
          {parsed && !posting && (
            <button
              onClick={clearAll}
              className="transition-opacity hover:opacity-70"
              style={{ color: "hsl(170 50% 15%)" }}
              aria-label="Clear upload"
            >
              <X className="h-3 w-3" />
            </button>
          )}
          <span
            className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
            style={{ backgroundColor: "hsl(170 50% 15% / 0.12)", color: "hsl(170 50% 15%)" }}
          >
            Live
          </span>
        </div>
      </LRDirectAtlasCardHeader>

      <div className="px-3 pb-3 pt-3 flex flex-col flex-1 space-y-2.5">
        {/* Banner */}
        <div className="rounded-md px-3 py-2 flex items-center gap-2" style={{ backgroundColor: "hsl(210 90% 50% / 0.08)" }}>
          <FileSpreadsheet className="h-3 w-3 shrink-0" style={{ color: "hsl(210 70% 42%)" }} />
          <span className="text-[11px] font-semibold" style={{ color: "hsl(210 70% 42%)" }}>
            Post many direct loads at once
          </span>
        </div>

        <p className="text-[11px] text-muted-foreground leading-relaxed">
          Download the LR Direct CSV template, fill in your loads, and upload to post in bulk.
          <span className="block mt-0.5 text-[10px]">Maximum {MAX_BULK_ROWS} rows per upload.</span>
        </p>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleDownload}
            className="text-[11px] font-semibold px-2.5 py-1 rounded-md border border-border bg-background hover:bg-muted transition-colors text-foreground inline-flex items-center gap-1.5"
          >
            <Download className="h-3 w-3" />
            Download Template
          </button>
          <button
            onClick={() => fileRef.current?.click()}
            disabled={posting}
            className="text-[11px] font-semibold px-2.5 py-1 rounded-md border border-border bg-background hover:bg-muted transition-colors text-foreground inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <Upload className="h-3 w-3" />
            Upload CSV
          </button>
          <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleFile} />
        </div>
        <p className="text-[10px] text-muted-foreground">CSV only · one row = one lane · post type &amp; routing are set below</p>

        {/* Post type (batch-level) */}
        <div className="space-y-1.5 rounded-md border border-border p-2">
          <span className="text-[9px] text-muted-foreground uppercase tracking-wide font-medium">Post this batch as</span>
          <div className="flex items-center gap-1">
            {([
              { key: "load", label: "Loads" },
              { key: "quote", label: "Quote requests" },
            ] as { key: DirectPostType; label: string }[]).map((o) => (
              <button
                key={o.key}
                onClick={() => setPostType(o.key)}
                disabled={posting}
                className={`text-[10px] font-semibold px-2 py-1 rounded-md border transition-colors ${
                  postType === o.key
                    ? "border-transparent text-white"
                    : "border-border bg-background text-muted-foreground hover:bg-muted"
                }`}
                style={postType === o.key ? { backgroundColor: "hsl(var(--brand-teal-deep))" } : undefined}
              >
                {o.label}
              </button>
            ))}
          </div>
          <p className="text-[10px] text-muted-foreground">
            Every row in the file posts as {postType === "load" ? "a load" : "a quote request"}.
          </p>
        </div>

        {/* Audience (Brokers / Carriers / Both) intentionally NOT implemented —
            requires schema + RLS + bid enforcement. Slot reserved here. */}

        {/* Routing */}
        <div className="space-y-1.5 rounded-md border border-border p-2">
          <span className="text-[9px] text-muted-foreground uppercase tracking-wide font-medium">Send batch to</span>
          <div className="flex items-center gap-1">
            {([
              { key: "private", label: "Private" },
              { key: "open", label: "Open market" },
              { key: "both", label: "Both" },
            ] as { key: DirectSendTo; label: string }[]).map((o) => (
              <button
                key={o.key}
                onClick={() => setSendTo(o.key)}
                disabled={posting}
                className={`text-[10px] font-semibold px-2 py-1 rounded-md border transition-colors ${
                  sendTo === o.key
                    ? "border-transparent text-white"
                    : "border-border bg-background text-muted-foreground hover:bg-muted"
                }`}
                style={sendTo === o.key ? { backgroundColor: "hsl(var(--brand-teal-deep))" } : undefined}
              >
                {o.label}
              </button>
            ))}
          </div>
          {needsNetwork && (
            <select
              value={defaultNetworkId}
              onChange={(e) => setNetworkId(e.target.value)}
              disabled={posting || networksLoading}
              className="w-full text-[11px] rounded-md border border-border bg-background px-2 py-1 text-foreground"
            >
              <option value="">Select a private network…</option>
              {networks.map((n) => (
                <option key={n.id} value={n.id}>
                  {n.name} ({n.member_count ?? 0} broker{(n.member_count ?? 0) !== 1 ? "s" : ""})
                </option>
              ))}
            </select>
          )}
          <p className="text-[10px] text-muted-foreground">
            Applies to every lane in the file — routing is no longer a CSV column.
          </p>
          {routingBlocked && (
            <p className="text-[10px] font-semibold flex items-center gap-1" style={{ color: "hsl(0 72% 45%)" }}>
              <Ban className="h-3 w-3 shrink-0" />
              Select a private network — private loads are never posted to the open market.
            </p>
          )}
        </div>

        {softWarn && (
          <p className="text-[10px] font-medium" style={{ color: "hsl(35 80% 42%)" }}>
            {softWarn}
          </p>
        )}

        {/* Stats */}
        <div className="grid grid-cols-3 gap-1.5">
          {stats.map((s) => (
            <div key={s.label} className="rounded-md px-2 py-1.5 bg-muted/50">
              <div className="flex items-center gap-1 mb-0.5">
                <s.icon className="h-2.5 w-2.5 text-muted-foreground" />
                <span className="text-[9px] text-muted-foreground uppercase tracking-wide truncate">{s.label}</span>
              </div>
              <span className="text-xs font-semibold text-foreground">{s.value}</span>
            </div>
          ))}
        </div>

        {/* Row errors */}
        {parsed && parsed.invalidRows.length > 0 && (
          <div className="space-y-1">
            <button
              onClick={() => setShowErrors((v) => !v)}
              className="text-[9px] uppercase tracking-wide font-medium text-muted-foreground hover:text-foreground"
            >
              {showErrors ? "Hide" : "Show"} {parsed.invalidRows.length} row error
              {parsed.invalidRows.length !== 1 ? "s" : ""}
            </button>
            {showErrors && (
              <div className="max-h-32 overflow-y-auto space-y-1">
                {parsed.invalidRows.map(({ row, errors }) => (
                  <div key={row.rowNumber} className="text-[10px] leading-snug">
                    <span className="font-semibold text-foreground">Row {row.rowNumber}</span>
                    <span className="text-muted-foreground"> — {errors.join("; ")}</span>
                  </div>
                ))}
              </div>
            )}
            <p className="text-[10px] text-muted-foreground">Fix those rows in your CSV and upload again.</p>
          </div>
        )}

        {/* Progress */}
        {posting && progress && (
          <div className="space-y-1">
            <div className="h-1.5 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full transition-all"
                style={{ width: `${pct}%`, backgroundColor: "hsl(var(--brand-teal-deep))" }}
              />
            </div>
            <div className="flex items-center justify-between text-[10px] text-muted-foreground">
              <span>
                Posting {progress.current} of {progress.total}…
              </span>
              <button onClick={cancel} className="font-semibold hover:text-foreground">
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Summary */}
        {summary && !posting && (
          <div className="space-y-1 rounded-md border border-border p-2">
            <div className="text-[11px] font-semibold text-foreground">
              {summary.posted} posted · {summary.failed} failed · {summary.skipped} skipped
              {summary.cancelled > 0 ? ` · ${summary.cancelled} cancelled` : ""}
            </div>
            <div className="max-h-32 overflow-y-auto space-y-0.5">
              {summary.rows.map((r) => (
                <div key={r.rowNumber} className="text-[10px] leading-snug flex items-start gap-1">
                  {r.ok ? (
                    <CheckCircle2 className="h-2.5 w-2.5 mt-0.5 shrink-0" style={{ color: "hsl(150 60% 38%)" }} />
                  ) : (
                    <AlertTriangle className="h-2.5 w-2.5 mt-0.5 shrink-0" style={{ color: "hsl(0 72% 45%)" }} />
                  )}
                  <span className="min-w-0">
                    <span className="font-semibold text-foreground">Row {r.rowNumber}</span>
                    <span className="text-muted-foreground">
                      {" "}
                      — {r.lane}
                      {r.ok ? ` · ${r.postType === "quote" ? "quote request sent" : "load posted"}` : ` · ${r.error}`}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="mt-auto pt-1">
          <button
            onClick={handlePost}
            disabled={posting || !parsed || parsed.validRows.length === 0}
            className="flex items-center gap-1 text-[11px] font-semibold w-full rounded-md px-3 py-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ backgroundColor: "hsl(170 85% 32% / 0.08)", color: "hsl(var(--brand-teal-deep))" }}
          >
            {posting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
            {posting
              ? "Posting…"
              : parsed
              ? `Post ${parsed.validRows.length} valid row${parsed.validRows.length !== 1 ? "s" : ""}`
              : "Upload a CSV to post"}
          </button>
        </div>
      </div>
    </LRDirectAtlasCard>
  );
}
